// A same-origin path to return to after login. Used by the pages an assistant
// (Claude, ChatGPT) sends users to — the OAuth consent screen and upload
// links: a signed-out user logs in and must land back there rather than on
// the workspace home.
//
// Two places navigate after a login — the login/SSO page itself and the
// PublicRoute guard on /auth, whose redirect fires last and would otherwise
// win. Both therefore *peek* at the stored path; the destination page clears
// it. Entries expire after ten minutes by default, matching the consent
// request's TTL; a page that stays usable longer passes its own lifetime
// (upload links: thirty minutes).
const KEY = 'postLoginReturnTo';
const DEFAULT_TTL_MS = 10 * 60 * 1000;

const isSafePath = (p: string) => p.startsWith('/') && !p.startsWith('//') && !p.includes('://');

export function rememberReturnTo(path: string, ttlMs = DEFAULT_TTL_MS) {
  if (!isSafePath(path)) return;
  sessionStorage.setItem(KEY, JSON.stringify({ path, expires: Date.now() + ttlMs }));
}

export function peekReturnTo(): string | null {
  const raw = sessionStorage.getItem(KEY);
  if (!raw) return null;
  try {
    const { path, expires, at } = JSON.parse(raw) as {
      path?: string;
      expires?: number;
      at?: number;
    };
    // Entries saved before `expires` existed carry their save time instead.
    const until = expires ?? (at ? at + DEFAULT_TTL_MS : 0);
    if (!path || !isSafePath(path) || Date.now() > until) {
      sessionStorage.removeItem(KEY);
      return null;
    }
    return path;
  } catch {
    sessionStorage.removeItem(KEY);
    return null;
  }
}

export function clearReturnTo() {
  sessionStorage.removeItem(KEY);
}

/** @deprecated prefer peekReturnTo + clearReturnTo; kept for call sites that navigate once. */
export function consumeReturnTo(): string | null {
  const p = peekReturnTo();
  clearReturnTo();
  return p;
}
